
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
    ArrowLeft,
    ArrowRight,
    BedDouble,
    CalendarDays,
    Check,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Clock3,
    CreditCard,
    Hotel,
    Image as ImageIcon,
    Info,
    LoaderCircle,
    MapPin,
    Maximize2,
    Minus,
    Plus,
    ShieldCheck,
    Sparkles,
    Star,
    Tag,
    Users,
    Wifi,
    Wind,
    Coffee,
    Waves,
    Dumbbell,
    Car,
    Utensils,
    X,
} from "lucide-react";

const API = "https://dubaibudgetivehotels.com/api";
const ROOM_IMG = "https://uaediscountedhotels.com/uploads/rooms";

const money = (value) =>
    new Intl.NumberFormat("en-AE", {
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

const dateToday = () => {
    const now = new Date();
    return [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
    ].join("-");
};

const nextDate = (date) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + 1);
    return [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, "0"),
        String(d.getDate()).padStart(2, "0"),
    ].join("-");
};

const parseArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];

    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed;
        } catch {
            return value.split(",").map((v) => v.trim()).filter(Boolean);
        }
    }

    return [];
};

const imageUrl = (image) => {
    const path =
        typeof image === "object"
            ? image?.url || image?.path || image?.image
            : image;

    if (!path || typeof path !== "string") return "";
    if (/^https?:\/\//i.test(path)) return path;
    if (path.startsWith("//")) return `https:${path}`;
    if (path.startsWith("/")) return path;

    return `${ROOM_IMG}/${path}`;
};

const plainText = (html) =>
    String(html || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/\s+/g, " ")
        .trim();

const IconForAmenity = ({ name }) => {
    const value = String(name).toLowerCase();

    if (value.includes("wifi")) return <Wifi size={19} />;
    if (value.includes("air")) return <Wind size={19} />;
    if (value.includes("coffee") || value.includes("breakfast"))
        return <Coffee size={19} />;
    if (value.includes("pool") || value.includes("beach"))
        return <Waves size={19} />;
    if (value.includes("gym") || value.includes("fitness"))
        return <Dumbbell size={19} />;
    if (value.includes("parking")) return <Car size={19} />;
    if (value.includes("restaurant")) return <Utensils size={19} />;

    return <Check size={19} />;
};

function SectionTitle({ eyebrow, title, description }) {
    return (
        <div className="mb-6">
            <p className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
                {eyebrow}
            </p>
            <h2 className="text-[26px] font-extrabold tracking-tight text-[#17324D] sm:text-[32px]">
                {title}
            </h2>
            {description && (
                <p className="mt-2 text-sm leading-6 text-[#718096]">
                    {description}
                </p>
            )}
        </div>
    );
}

export default function RoomDetail() {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [galleryIndex, setGalleryIndex] = useState(0);
    const [showGallery, setShowGallery] = useState(false);
    const [showBooking, setShowBooking] = useState(false);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);

    const [bookingLoading, setBookingLoading] = useState(false);
    const [bookingError, setBookingError] = useState("");

    const today = dateToday();

    useEffect(() => {
        let cancelled = false;

        async function fetchRoom() {
            setLoading(true);
            setError("");
            setRoom(null);
            setGalleryIndex(0);

            try {
                const response = await axios.get(`${API}/rooms/${roomId}`);

                if (!cancelled) {
                    const data = response.data?.data || response.data;
                    setRoom(data);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setError("Unable to load room details.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (roomId) fetchRoom();
        else {
            setLoading(false);
            setError("Room not found.");
        }

        return () => {
            cancelled = true;
        };
    }, [roomId]);

    const images = useMemo(() => {
        if (!room) return [];

        const raw = [room.image, ...parseArray(room.images)];
        return [...new Set(raw.map(imageUrl).filter(Boolean))];
    }, [room]);

    const amenities = useMemo(() => {
        return parseArray(room?.amenities)
            .map((item) =>
                typeof item === "object"
                    ? item?.name || item?.title || ""
                    : String(item)
            )
            .filter(Boolean);
    }, [room]);

    const seasonalPrices = useMemo(
        () => parseArray(room?.seasonal_prices),
        [room]
    );

    const selectedDate = checkIn || today;

    const activeSeason = useMemo(() => {
        return (
            seasonalPrices.find((season) => {
                const start = String(season.start_date || "").slice(0, 10);
                const end = String(season.end_date || "").slice(0, 10);
                return selectedDate >= start && selectedDate <= end;
            }) || null
        );
    }, [seasonalPrices, selectedDate]);

    const pricing = useMemo(() => {
        const regular = Number(activeSeason?.price ?? room?.price ?? 0);
        const discount = Number(room?.discount_price || 0);

        const nightly =
            discount > 0 && discount < regular ? discount : regular;

        const savings = Math.max(regular - nightly, 0);
        const discountPercent =
            regular > 0 ? Math.round((savings / regular) * 100) : 0;

        const service = nightly * 0.1;
        const vat = nightly * 0.05;
        const tourism = Number(room?.hotel?.rating) === 5 ? 20 : 10;

        const finalNightly = nightly + service + vat + tourism;

        return {
            regular,
            nightly,
            savings,
            discountPercent,
            service,
            vat,
            tourism,
            finalNightly,
        };
    }, [room, activeSeason]);

    const nights = useMemo(() => {
        if (!checkIn || !checkOut || checkOut <= checkIn) return 1;

        const start = Date.parse(`${checkIn}T12:00:00`);
        const end = Date.parse(`${checkOut}T12:00:00`);

        return Math.max(1, Math.round((end - start) / 86400000));
    }, [checkIn, checkOut]);

    const total = Math.round(pricing.finalNightly * nights);

    useEffect(() => {
        if (!showGallery && !showBooking) return;

        const oldOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setShowGallery(false);
                setShowBooking(false);
            }

            if (showGallery && images.length > 1) {
                if (event.key === "ArrowRight") {
                    setGalleryIndex((i) => (i + 1) % images.length);
                }
                if (event.key === "ArrowLeft") {
                    setGalleryIndex((i) => (i - 1 + images.length) % images.length);
                }
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = oldOverflow;
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [showGallery, showBooking, images.length]);

    const previousImage = () =>
        setGalleryIndex((i) => (i - 1 + images.length) % images.length);

    const nextImage = () =>
        setGalleryIndex((i) => (i + 1) % images.length);

    const openGallery = (index) => {
        if (!images.length) return;
        setGalleryIndex(index);
        setShowGallery(true);
    };

    const handleBooking = async (event) => {
        event.preventDefault();
        setBookingError("");

        if (room?.is_available === false) {
            setBookingError("This room is currently unavailable.");
            return;
        }

        if (!name.trim() || !email.trim() || !phone.trim()) {
            setBookingError("Please complete your contact information.");
            return;
        }

        if (!checkIn || !checkOut || checkIn < dateToday() || checkOut <= checkIn) {
            setBookingError("Please choose valid check-in and check-out dates.");
            return;
        }

        const guestCount = Number(guests);
        if (
            !Number.isInteger(guestCount) ||
            guestCount < 1 ||
            (room.capacity && guestCount > Number(room.capacity))
        ) {
            setBookingError("Please select a valid number of guests.");
            return;
        }

        if (!executeRecaptcha) {
            setBookingError("reCAPTCHA is not ready. Please try again.");
            return;
        }

        try {
            setBookingLoading(true);

            const captcha = await executeRecaptcha("room_booking");

            let user = null;
            try {
                user = JSON.parse(localStorage.getItem("user") || "null");
            } catch {
                user = null;
            }

            const bookingData = {
                user_id: user?.id || null,
                hotel_id: room.hotel?.id ?? room.hotel_id,
                room_id: room.id,
                room_name: room.name || `Room ${room.room_number}`,
                room_price: room.price,
                discount_price: room.discount_price || null,
                name: name.trim(),
                email: email.trim(),
                phone: phone.trim(),
                check_in: checkIn,
                check_out: checkOut,
                guests: guestCount,
                season_name: activeSeason?.season_name || null,
                season_start: activeSeason?.start_date || null,
                season_end: activeSeason?.end_date || null,
                total_amount: total,
                nights,
                captcha,
            };

            const response = await axios.post(
                `${API}/hotel-payment`,
                bookingData
            );

            const paymentUrl = response.data?.payment_url;

            if (!paymentUrl) {
                setBookingError("Payment link was not received.");
                return;
            }

            const url = new URL(paymentUrl, window.location.origin);

            if (!["https:", "http:"].includes(url.protocol)) {
                throw new Error("Invalid payment URL.");
            }

            window.location.assign(url.href);
        } catch (err) {
            console.error(err);
            setBookingError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Booking failed. Please try again."
            );
        } finally {
            setBookingLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F5FAF7]">
                <div className="text-center">
                    <LoaderCircle
                        size={42}
                        className="mx-auto animate-spin text-[#168B43]"
                    />
                    <p className="mt-4 text-sm text-[#718096]">
                        Loading your room...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !room) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F5FAF7] px-4">
                <div className="w-full max-w-md rounded-2xl border border-[#DCEAE1] bg-white p-10 text-center">
                    <Info size={40} className="mx-auto text-[#168B43]" />
                    <h2 className="mt-4 text-2xl font-extrabold text-[#17324D]">
                        Room Not Found
                    </h2>
                    <p className="mt-2 text-sm text-[#718096]">
                        {error || "This room is unavailable."}
                    </p>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-6 rounded-xl bg-[#168B43] px-6 py-3 text-sm font-bold text-white"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const roomName = room.name || `Room ${room.room_number || ""}`.trim();
    const hotelPath = room.hotel?.slug
        ? `/hotels/${room.hotel.slug}`
        : "/hotels";

    const unavailable = room.is_available === false;

    const roomFacts = [
        {
            icon: Users,
            label: "Capacity",
            value: room.capacity ? `Up to ${room.capacity} guests` : "Not specified",
        },
        {
            icon: BedDouble,
            label: "Bed Type",
            value: room.bed_type || "Not specified",
        },
        {
            icon: Maximize2,
            label: "Room Size",
            value: room.size_sqm ? `${room.size_sqm} m²` : "Not specified",
        },
        {
            icon: ShieldCheck,
            label: "Availability",
            value: unavailable ? "Unavailable" : "Check availability",
        },
    ];

    return (
        <>
            <Helmet>
                <title>{room.meta_title || roomName}</title>
                <meta
                    name="description"
                    content={plainText(
                        room.meta_description || room.description
                    )}
                />
                <meta property="og:title" content={room.meta_title || roomName} />
                <meta
                    property="og:description"
                    content={plainText(
                        room.meta_description || room.description
                    )}
                />
                {images[0] && (
                    <meta property="og:image" content={images[0]} />
                )}
            </Helmet>

            <main className="min-h-screen bg-[#F5FAF7] pb-28 pt-24 text-[#17324D]">
                {/* PAGE HEADER */}
                <section className="border-b border-[#E2ECE6] bg-white">
                    <div className="mx-auto w-full max-w-[1536px] px-4 py-8 sm:px-6 lg:px-8">
                        <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
                            <nav className="flex flex-wrap items-center gap-2 text-xs text-[#718096]">
                                <Link to="/" className="hover:text-[#168B43]">
                                    Home
                                </Link>
                                <ChevronRight size={13} />
                                <Link to={hotelPath} className="hover:text-[#168B43]">
                                    {room.hotel?.name || "Hotel"}
                                </Link>
                                <ChevronRight size={13} />
                                <span className="font-bold text-[#17324D]">
                                    {roomName}
                                </span>
                            </nav>

                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center gap-2 rounded-full border border-[#DCEAE1] px-4 py-2 text-xs font-bold transition hover:border-[#168B43] hover:text-[#168B43]"
                            >
                                <ArrowLeft size={15} />
                                Back
                            </button>
                        </div>

                        <div className="flex flex-wrap items-end justify-between gap-6">
                            <div className="max-w-3xl">
                                <div className="mb-3 flex flex-wrap gap-2">
                                    {room.room_number && (
                                        <span className="rounded-full bg-[#E8F7EE] px-3 py-1.5 text-[11px] font-bold text-[#168B43]">
                                            Room {room.room_number}
                                        </span>
                                    )}

                                    {room.hotel?.rating && (
                                        <span className="flex items-center gap-1 rounded-full bg-[#FFF7E6] px-3 py-1.5 text-[11px] font-bold">
                                            <Star
                                                size={13}
                                                className="fill-[#F3AE2C] text-[#F3AE2C]"
                                            />
                                            {room.hotel.rating} Star Hotel
                                        </span>
                                    )}

                                    {pricing.discountPercent > 0 && (
                                        <span className="flex items-center gap-1 rounded-full bg-[#E8F7EE] px-3 py-1.5 text-[11px] font-bold text-[#168B43]">
                                            <Tag size={12} />
                                            {pricing.discountPercent}% OFF
                                        </span>
                                    )}
                                </div>

                                <h1 className="text-[34px] font-extrabold leading-tight tracking-[-0.04em] sm:text-[46px]">
                                    {roomName}
                                </h1>

                                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-sm text-[#718096]">
                                    <span className="flex items-center gap-2">
                                        <MapPin size={16} className="text-[#168B43]" />
                                        {room.hotel?.name || "Dubai Hotel"}
                                    </span>

                                    {room.capacity && (
                                        <span className="flex items-center gap-2">
                                            <Users size={16} className="text-[#168B43]" />
                                            Up to {room.capacity} Guests
                                        </span>
                                    )}

                                    {room.bed_type && (
                                        <span className="flex items-center gap-2">
                                            <BedDouble size={16} className="text-[#168B43]" />
                                            {room.bed_type}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-5 rounded-2xl border border-[#E2ECE6] bg-[#F8FBF9] p-5">
                                <div>
                                    <p className="text-xs font-medium text-[#718096]">
                                        Estimated Price / Night
                                    </p>
                                    <p className="mt-1 text-3xl font-extrabold text-[#168B43]">
                                        <span className="mr-1 text-sm">AED</span>
                                        {money(pricing.finalNightly)}
                                    </p>
                                    <p className="mt-1 text-[11px] text-[#94A3B8]">
                                        Including estimated fees
                                    </p>
                                </div>

                                <button
                                    onClick={() => setShowBooking(true)}
                                    disabled={unavailable}
                                    className="rounded-xl bg-[#168B43] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#087D37] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Book Now
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* GALLERY */}
                <section className="mx-auto w-full max-w-[1536px] px-4 pt-7 sm:px-6 lg:px-8">
                    <div className="grid gap-3 overflow-hidden rounded-[20px] md:grid-cols-[1.7fr_0.8fr]">
                        <button
                            type="button"
                            onClick={() => openGallery(0)}
                            disabled={!images.length}
                            className="group relative h-[300px] overflow-hidden bg-[#DCEAE1] text-left sm:h-[410px] lg:h-[480px]"
                        >
                            {images[0] ? (
                                <img
                                    src={images[0]}
                                    alt={roomName}
                                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                />
                            ) : (
                                <div className="flex h-full items-center justify-center text-[#8CA79A]">
                                    <ImageIcon size={40} />
                                </div>
                            )}

                            {images.length > 0 && (
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#064E3B]/70 to-transparent p-5">
                                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-[#17324D]">
                                        <Maximize2 size={14} />
                                        View All Photos · {images.length}
                                    </span>
                                </div>
                            )}
                        </button>

                        <div className="grid grid-cols-2 gap-3 md:grid-cols-1 md:grid-rows-2">
                            {[1, 2].map((index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => openGallery(index)}
                                    disabled={!images[index]}
                                    className="relative min-h-[135px] overflow-hidden bg-[#DCEAE1]"
                                >
                                    {images[index] ? (
                                        <>
                                            <img
                                                src={images[index]}
                                                alt={`${roomName} photo ${index + 1}`}
                                                className="h-full w-full object-cover transition duration-500 hover:scale-105"
                                            />
                                            {index === 2 && images.length > 3 && (
                                                <span className="absolute inset-0 flex items-center justify-center bg-[#064E3B]/60 text-lg font-extrabold text-white">
                                                    +{images.length - 3} More Photos
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <div className="flex h-full items-center justify-center text-[#8CA79A]">
                                            <ImageIcon size={25} />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ROOM FACTS */}
                    <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                        {roomFacts.map(({ icon: Icon, label, value }) => (
                            <div
                                key={label}
                                className="flex items-center gap-3 rounded-xl border border-[#E2ECE6] bg-white p-4"
                            >
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                    <Icon size={20} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                                        {label}
                                    </p>
                                    <p className="mt-1 truncate text-xs font-extrabold">
                                        {value}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* MAIN CONTENT */}
                <section className="mx-auto mt-10 w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_390px]">
                        <div className="min-w-0 space-y-8">
                            {/* ABOUT */}
                            <section className="rounded-[20px] border border-[#E2ECE6] bg-white p-6 sm:p-8">
                                <SectionTitle
                                    eyebrow="Discover Your Room"
                                    title="About This Room"
                                    description="Everything you need to know about your accommodation."
                                />

                                <p className="whitespace-pre-line text-sm leading-8 text-[#64748B]">
                                    {plainText(room.description) ||
                                        "Enjoy a comfortable stay with convenient facilities in Dubai."}
                                </p>
                            </section>

                            {/* AMENITIES */}

                            <section
                                aria-labelledby="room-amenities-title"
                                className="rounded-[20px] border border-[#E2ECE6] bg-white p-6 sm:p-8"
                            >
                                <SectionTitle
                                    eyebrow="Comfort & Convenience"
                                    title="Room Amenities"
                                    description="Explore the facilities listed for this room."
                                />

                                {amenities.length > 0 ? (
                                    <ul className="grid gap-3 sm:grid-cols-2">
                                        {amenities.map((amenity, index) => (
                                            <li
                                                key={`${amenity}-${index}`}
                                                className="
            flex items-center gap-3
            rounded-xl
            border border-[#E6F0E9]
            bg-[#F8FBF9]
            p-4
          "
                                            >
                                                {/* Decorative Amenity Icon */}
                                                <span
                                                    aria-hidden="true"
                                                    className="
              flex h-10 w-10 shrink-0
              items-center justify-center
              rounded-xl
              bg-[#E8F7EE]
              text-[#168B43]
            "
                                                >
                                                    <IconForAmenity name={amenity} />
                                                </span>

                                                {/* Accessible Amenity Name */}
                                                <span className="text-sm font-semibold text-[#17324D]">
                                                    {amenity}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-[#718096]">
                                        No amenities have been listed for this room.
                                    </p>
                                )}
                            </section>


                            {/* POLICIES */}
                            <section className="rounded-[20px] border border-[#E2ECE6] bg-white p-6 sm:p-8">
                                <SectionTitle
                                    eyebrow="Before You Book"
                                    title="Room Policies"
                                    description="Review the applicable conditions for your stay."
                                />

                                <div className="grid gap-3 sm:grid-cols-2">
                                    {[
                                        {
                                            label: "Cancellation",
                                            value: room.cancellation_policy || "Policy applies",
                                        },
                                        {
                                            label: "Breakfast",
                                            value:
                                                room.breakfast_included == null
                                                    ? "Not specified"
                                                    : room.breakfast_included
                                                        ? "Included"
                                                        : "Not Included",
                                        },
                                        {
                                            label: "Smoking",
                                            value:
                                                room.smoking_allowed == null
                                                    ? "Not specified"
                                                    : room.smoking_allowed
                                                        ? "Allowed"
                                                        : "Not Allowed",
                                        },
                                        {
                                            label: "Availability",
                                            value: unavailable
                                                ? "Unavailable"
                                                : "Subject to confirmation",
                                        },
                                    ].map((policy) => (
                                        <div
                                            key={policy.label}
                                            className="rounded-xl border border-[#E6F0E9] bg-[#F8FBF9] p-5"
                                        >
                                            <p className="text-xs text-[#94A3B8]">
                                                {policy.label}
                                            </p>
                                            <p className="mt-2 text-sm font-extrabold text-[#17324D]">
                                                {policy.value}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* PROMOTIONAL STRIP */}
                            <section className="relative overflow-hidden rounded-[20px] bg-gradient-to-br from-[#064E3B] to-[#168B43] p-7 text-white sm:p-9">
                                <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-white/5" />
                                <div className="relative">
                                    <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                                        Your Dubai Escape
                                    </p>
                                    <h2 className="mt-3 max-w-xl text-[27px] font-extrabold leading-tight !text-white sm:text-[35px]">
                                        Make your next Dubai stay memorable.
                                    </h2>
                                    <p className="mt-3 max-w-xl text-sm leading-7 text-white/75">
                                        Compare your room details, review the estimated charges,
                                        and continue to the reservation process.
                                    </p>
                                    <button
                                        onClick={() => setShowBooking(true)}
                                        disabled={unavailable}
                                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-extrabold text-[#064E3B] disabled:opacity-50"
                                    >
                                        Reserve Your Room
                                        <ArrowRight size={17} />
                                    </button>
                                </div>
                            </section>
                        </div>

                        {/* STICKY BOOKING SUMMARY */}
                        <aside className="lg:sticky lg:top-28">
                            <div className="overflow-hidden rounded-[20px] border border-[#DCEAE1] bg-white shadow-[0_15px_45px_rgba(6,78,59,0.09)]">
                                <div className="bg-gradient-to-br from-[#064E3B] to-[#168B43] p-6 text-white">
                                    <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                                        Room Reservation
                                    </p>
                                    <h3 className="mt-2 text-2xl font-extrabold text-white">
                                        Your Stay Starts Here
                                    </h3>
                                    <p className="mt-2 text-xs text-white/75">
                                        Estimated pricing for your selected room.
                                    </p>
                                    <div className="mt-6 flex items-end gap-2">
                                        <span className="text-4xl font-extrabold">
                                            {money(pricing.finalNightly)}
                                        </span>
                                        <span className="mb-1 text-xs text-white/75">
                                            AED / night
                                        </span>
                                    </div>
                                </div>

                                <div className="p-5 sm:p-6">
                                    {pricing.discountPercent > 0 && (
                                        <div className="mb-5 flex items-center justify-between rounded-xl bg-[#E8F7EE] p-4">
                                            <div className="flex items-center gap-2 text-[#168B43]">
                                                <Tag size={18} />
                                                <span className="text-sm font-extrabold">
                                                    {pricing.discountPercent}% OFF
                                                </span>
                                            </div>
                                            <span className="text-xs font-bold text-[#168B43]">
                                                Save AED {money(pricing.savings)}
                                            </span>
                                        </div>
                                    )}

                                    {activeSeason && (
                                        <div className="mb-5 flex items-center gap-3 rounded-xl border border-[#DCEAE1] p-3 text-xs text-[#168B43]">
                                            <Sparkles size={17} />
                                            {activeSeason.season_name || "Seasonal Rate Applied"}
                                        </div>
                                    )}

                                    <h4 className="mb-5 text-sm font-extrabold">
                                        Price Breakdown
                                    </h4>

                                    <div className="space-y-4 text-xs text-[#64748B]">
                                        {[
                                            ["Room Rate / Night", pricing.nightly],
                                            ["Service Charge (10%)", pricing.service],
                                            ["VAT (5%)", pricing.vat],
                                            ["Tourism Fee / Night", pricing.tourism],
                                        ].map(([label, value]) => (
                                            <div key={label} className="flex justify-between gap-3">
                                                <span>{label}</span>
                                                <span className="font-bold text-[#17324D]">
                                                    AED {money(value)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-6 rounded-xl border border-[#CFE6D8] bg-[#F5FAF7] p-5">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-xs text-[#64748B]">
                                                    Estimated Nightly Total
                                                </p>
                                                <p className="mt-1 text-[11px] text-[#94A3B8]">
                                                    Including estimated charges
                                                </p>
                                            </div>
                                            <p className="text-xl font-extrabold text-[#168B43]">
                                                AED {money(pricing.finalNightly)}
                                            </p>
                                        </div>

                                        {checkIn && checkOut && checkOut > checkIn && (
                                            <div className="mt-4 flex justify-between border-t border-[#DCEAE1] pt-4">
                                                <div>
                                                    <p className="text-xs font-bold">
                                                        Estimated Stay Total
                                                    </p>
                                                    <p className="text-[11px] text-[#94A3B8]">
                                                        {nights} Night{nights > 1 ? "s" : ""}
                                                    </p>
                                                </div>
                                                <p className="text-lg font-extrabold text-[#168B43]">
                                                    AED {money(total)}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => setShowBooking(true)}
                                        disabled={unavailable}
                                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#168B43] py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {unavailable ? "Room Unavailable" : "Reserve This Room"}
                                        {!unavailable && <ArrowRight size={17} />}
                                    </button>

                                    <p className="mt-4 flex items-center justify-center gap-2 text-center text-[11px] text-[#94A3B8]">
                                        <ShieldCheck size={15} className="text-[#168B43]" />
                                        Continue to payment
                                    </p>

                                    {room.cancellation_policy && (
                                        <p className="mt-4 border-t border-[#EAF1EC] pt-4 text-center text-xs text-[#64748B]">
                                            Cancellation: {room.cancellation_policy}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="rounded-xl border border-[#E2ECE6] bg-white p-4 text-center">
                                    <ShieldCheck size={22} className="mx-auto text-[#168B43]" />
                                    <p className="mt-2 text-xs font-bold">Booking Support</p>
                                </div>
                                <div className="rounded-xl border border-[#E2ECE6] bg-white p-4 text-center">
                                    <CreditCard size={22} className="mx-auto text-[#168B43]" />
                                    <p className="mt-2 text-xs font-bold">Payment Options</p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>
            </main>

            {/* MOBILE BOOKING BAR */}
            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#DCEAE1] bg-white p-3 shadow-lg lg:hidden">
                <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-bold text-[#94A3B8]">
                            Estimated / Night
                        </p>
                        <p className="text-lg font-extrabold text-[#168B43]">
                            AED {money(pricing.finalNightly)}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowBooking(true)}
                        disabled={unavailable}
                        className="rounded-xl bg-[#168B43] px-5 py-3 text-xs font-extrabold text-white disabled:opacity-50"
                    >
                        {unavailable ? "Unavailable" : "Reserve Now"}
                    </button>
                </div>
            </div>

            {/* FULLSCREEN GALLERY */}
            {showGallery && images.length > 0 && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Room photo gallery"
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 p-4"
                >
                    <button
                        type="button"
                        onClick={() => setShowGallery(false)}
                        aria-label="Close gallery"
                        className="absolute right-5 top-5 rounded-full bg-white/15 p-3 text-white"
                    >
                        <X size={22} />
                    </button>

                    <span className="absolute left-5 top-6 text-sm font-bold text-white">
                        {galleryIndex + 1} / {images.length}
                    </span>

                    {images.length > 1 && (
                        <button
                            onClick={previousImage}
                            aria-label="Previous image"
                            className="absolute left-3 rounded-full bg-white/15 p-3 text-white sm:left-8"
                        >
                            <ChevronLeft size={24} />
                        </button>
                    )}

                    <img
                        src={images[galleryIndex]}
                        alt={`${roomName} photo ${galleryIndex + 1}`}
                        className="max-h-[85vh] max-w-[82vw] object-contain"
                    />

                    {images.length > 1 && (
                        <button
                            onClick={nextImage}
                            aria-label="Next image"
                            className="absolute right-3 rounded-full bg-white/15 p-3 text-white sm:right-8"
                        >
                            <ChevronRight size={24} />
                        </button>
                    )}
                </div>
            )}

            {/* BOOKING MODAL */}
            {showBooking && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Room reservation"
                    className="fixed inset-0 z-[9999] overflow-y-auto bg-[#061D33]/75 px-3 py-6 backdrop-blur-sm sm:px-5"
                >
                    <div className="flex min-h-full items-center justify-center">
                        <div className="relative w-full max-w-2xl overflow-hidden rounded-[22px] bg-white shadow-2xl">
                            <div className="relative bg-gradient-to-br from-[#064E3B] to-[#168B43] p-6 text-white sm:p-8">
                                <button
                                    type="button"
                                    onClick={() => setShowBooking(false)}
                                    aria-label="Close booking"
                                    className="absolute right-5 top-5 rounded-full bg-white/15 p-2.5 text-white"
                                >
                                    <X size={20} />
                                </button>

                                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                                    Complete Your Reservation
                                </p>
                                <h2 className="mt-3 pr-10 text-[26px] font-extrabold !text-white sm:text-[32px]">
                                    Book {roomName}
                                </h2>
                                <p className="mt-2 text-xs leading-6 text-white/75">
                                    Enter your details to continue to the payment process.
                                </p>
                            </div>

                            <form onSubmit={handleBooking} className="space-y-6 p-5 sm:p-8">
                                <div>
                                    <h3 className="mb-4 flex items-center gap-2 text-sm font-extrabold">
                                        <Users size={18} className="text-[#168B43]" />
                                        Guest Information
                                    </h3>

                                    <div className="grid gap-4 sm:grid-cols-2">
                                        {[
                                            {
                                                label: "Full Name",
                                                type: "text",
                                                value: name,
                                                setter: setName,
                                                placeholder: "Your full name",
                                                autoComplete: "name",
                                            },
                                            {
                                                label: "Email Address",
                                                type: "email",
                                                value: email,
                                                setter: setEmail,
                                                placeholder: "you@example.com",
                                                autoComplete: "email",
                                            },
                                            {
                                                label: "Phone Number",
                                                type: "tel",
                                                value: phone,
                                                setter: setPhone,
                                                placeholder: "+971 50 000 0000",
                                                autoComplete: "tel",
                                            },
                                        ].map((field) => (
                                            <label key={field.label} className="block">
                                                <span className="mb-2 block text-xs font-bold text-[#64748B]">
                                                    {field.label}
                                                </span>
                                                <input
                                                    type={field.type}
                                                    required
                                                    value={field.value}
                                                    onChange={(e) => field.setter(e.target.value)}
                                                    placeholder={field.placeholder}
                                                    autoComplete={field.autoComplete}
                                                    className="w-full rounded-xl border border-[#DCEAE1] px-4 py-3 text-sm outline-none focus:border-[#168B43]"
                                                />
                                            </label>
                                        ))}

                                        <div>
                                            <p className="mb-2 text-xs font-bold text-[#64748B]">
                                                Number of Guests
                                            </p>
                                            <div className="flex items-center justify-between rounded-xl border border-[#DCEAE1] px-4 py-2">
                                                <span className="text-sm font-bold">
                                                    {guests} Guest{guests > 1 ? "s" : ""}
                                                </span>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setGuests((v) => Math.max(1, v - 1))}
                                                        className="rounded-lg bg-[#E8F7EE] p-2 text-[#168B43]"
                                                    >
                                                        <Minus size={15} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setGuests((v) =>
                                                                room.capacity
                                                                    ? Math.min(Number(room.capacity), v + 1)
                                                                    : v + 1
                                                            )
                                                        }
                                                        className="rounded-lg bg-[#E8F7EE] p-2 text-[#168B43]"
                                                    >
                                                        <Plus size={15} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-[#EAF1EC] pt-6">
                                    <h3 className="mb-4 flex items-center gap-2 text-sm font-extrabold">
                                        <CalendarDays size={18} className="text-[#168B43]" />
                                        Your Stay
                                    </h3>

                                    <div className="grid grid-cols-2 gap-3">
                                        <label className="block">
                                            <span className="mb-2 block text-xs font-bold text-[#64748B]">
                                                Check-in
                                            </span>
                                            <input
                                                type="date"
                                                required
                                                min={today}
                                                value={checkIn}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setCheckIn(value);
                                                    if (checkOut && checkOut <= value) setCheckOut("");
                                                }}
                                                className="w-full min-w-0 rounded-xl border border-[#DCEAE1] px-2 py-3 text-xs outline-none focus:border-[#168B43]"
                                            />
                                        </label>

                                        <label className="block">
                                            <span className="mb-2 block text-xs font-bold text-[#64748B]">
                                                Check-out
                                            </span>
                                            <input
                                                type="date"
                                                required
                                                min={nextDate(checkIn || today)}
                                                value={checkOut}
                                                onChange={(e) => setCheckOut(e.target.value)}
                                                className="w-full min-w-0 rounded-xl border border-[#DCEAE1] px-2 py-3 text-xs outline-none focus:border-[#168B43]"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="rounded-xl border border-[#CFE6D8] bg-[#F5FAF7] p-5">
                                    <div className="mb-4 flex items-center justify-between">
                                        <h3 className="text-sm font-extrabold">
                                            Reservation Summary
                                        </h3>
                                        <span className="rounded-full bg-[#E8F7EE] px-3 py-1 text-[11px] font-bold text-[#168B43]">
                                            {nights} Night{nights > 1 ? "s" : ""}
                                        </span>
                                    </div>

                                    <div className="space-y-3 text-xs text-[#64748B]">
                                        {[
                                            ["Room Rate / Night", pricing.nightly],
                                            ["Service Charge / Night", pricing.service],
                                            ["VAT / Night", pricing.vat],
                                            ["Tourism Fee / Night", pricing.tourism],
                                        ].map(([label, amount]) => (
                                            <div key={label} className="flex justify-between gap-3">
                                                <span>{label}</span>
                                                <span className="font-bold text-[#17324D]">
                                                    AED {money(amount)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-5 flex items-center justify-between border-t border-[#DCEAE1] pt-4">
                                        <div>
                                            <p className="text-sm font-extrabold">
                                                Estimated Total
                                            </p>
                                            <p className="text-[11px] text-[#94A3B8]">
                                                {nights} night stay
                                            </p>
                                        </div>
                                        <p className="text-2xl font-extrabold text-[#168B43]">
                                            AED {money(total)}
                                        </p>
                                    </div>
                                </div>

                                {bookingError && (
                                    <p
                                        role="alert"
                                        className="rounded-xl bg-red-50 p-4 text-xs text-red-700"
                                    >
                                        {bookingError}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    disabled={bookingLoading || unavailable}
                                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#168B43] py-4 text-sm font-extrabold text-white hover:bg-[#087D37] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {bookingLoading ? (
                                        <>
                                            <LoaderCircle size={18} className="animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            Confirm & Continue to Payment
                                            <ArrowRight size={17} />
                                        </>
                                    )}
                                </button>

                                <p className="text-center text-[11px] leading-5 text-[#94A3B8]">
                                    Your reservation is subject to the applicable room
                                    policies and final payment confirmation.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
